import queryString from "query-string";
import { isLegacyDoubleDropD } from "./chartOptions.js";
import { guitarSetups, mainActions, selectNpsValues } from ".";
import { decompressUrlSafe } from "../components/code/lzma-url.mjs";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isNumberString = (value) =>
  typeof value === "string" && value.trim() !== "" && Number.isFinite(+value);

const parseChart = (stringArgs) => {
  let obj = queryString.parse(stringArgs);
  if ("comp" in obj) {
    if (typeof obj.comp !== "string" || obj.comp === "") return null;
    obj = queryString.parse(decompressUrlSafe(obj.comp));
  }

  // query-string represents a single tuning interval as a scalar (two strings).
  const tuningValues = typeof obj.t === "string" ? [obj.t] : obj.t;

  if (
    !Array.isArray(obj.i) || obj.i.length !== 12 ||
    !obj.i.every((value) => value === "t" || value === "f") ||
    !Array.isArray(obj.ic) || obj.ic.length !== 12 ||
    !obj.ic.every((value) => typeof value === "string" && value !== "") ||
    !Array.isArray(tuningValues) || tuningValues.length < 1 || tuningValues.length > 20 ||
    !tuningValues.every((value) =>
      isNumberString(value) && Number.isInteger(+value) && +value > 0 && +value <= 127
    ) ||
    typeof obj.gs !== "string" ||
    !Object.prototype.hasOwnProperty.call(guitarSetups, obj.gs) ||
    !selectNpsValues.includes(obj.nps) ||
    typeof obj.tt !== "string" || typeof obj.in !== "string" ||
    !isNumberString(obj.is) || +obj.is <= 0 ||
    typeof obj.rd !== "string" ||
    ("mr" in obj && (!isNumberString(obj.mr) ||
      !Number.isInteger(+obj.mr) || +obj.mr < 0 || +obj.mr > 127))
  ) return null;

  const tuning = tuningValues.map(Number);
  // Preset tuning is applied by the reducer; custom tuning comes from the URL.
  const legacyDoubleDropD = isLegacyDoubleDropD(obj.gs, tuning);
  if (obj.gs !== "custom" && !legacyDoubleDropD &&
      JSON.stringify(tuning) !== JSON.stringify(guitarSetups[obj.gs][1])) {
    return null;
  }

  const refineData = JSON.parse(obj.rd);
  if (!isObject(refineData) || !Array.isArray(refineData.perChartData) ||
      !refineData.perChartData.every((chart) =>
        isObject(chart) && Array.isArray(chart.notesPerString) &&
        chart.notesPerString.length === tuning.length + 1 &&
        chart.notesPerString.every((count) => Number.isSafeInteger(count) && count >= 0) &&
        Number.isSafeInteger(chart.notesOffset) &&
        (!Object.prototype.hasOwnProperty.call(chart, "omittedNotes") ||
          (Array.isArray(chart.omittedNotes) && chart.omittedNotes.every(id =>
            Number.isSafeInteger(id) && id >= 0 && id < chart.notesPerString.reduce((sum, count) => sum + count, 0)
          ) && new Set(chart.omittedNotes).size === chart.omittedNotes.length))
      )) {
    return null;
  }

  if (legacyDoubleDropD) {
    obj.gs = "custom";
    // The old preset always used D2, even before URLs included a MIDI root.
    obj.mr = "38";
  }
  return { obj, tuning, refineData };
};

const UnpickleFromQueryString = (stringArgs, dispatch) => {
  let chart;
  try {
    chart = parseChart(stringArgs);
  } catch {
    // Invalid compression and JSON must not escape into the React lifecycle.
    return false;
  }
  if (!chart) return false;

  // Validate everything before applying any changes to the current chart.
  const { obj, tuning, refineData } = chart;
  dispatch(mainActions.setAllIntervals(obj.i.map((value) => value === "t")));
  dispatch(mainActions.setAllIntervalColors(obj.ic));
  dispatch(mainActions.setGuitarSetup(obj.gs));
  dispatch(mainActions.setCustomTuning(tuning));
  // Older links predate the MIDI root note field.
  dispatch(mainActions.setMidiRootNote("mr" in obj ? +obj.mr : 40));
  dispatch(mainActions.setTitle(obj.tt));
  dispatch(mainActions.setIntroduction(obj.in));
  dispatch(mainActions.setImageScale(+obj.is));
  dispatch(mainActions.setSelectNps(obj.nps));
  dispatch(mainActions.setRefineData(refineData));
  return true;
};

export default UnpickleFromQueryString;
